import { FunctionComponent, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import Link from '@atlaskit/link';
import { Link as RouterLink } from 'react-resource-router';
import { ROUTES } from '@app/navigation';
import { CharlieTable, TableState } from '@atlassian/charlie-table';
import type { ColumnDef } from '@atlassian/charlie-table/tanstack-helpers';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import EmptyState from '@atlaskit/empty-state';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import type {
  AppUpdatesContextStateType,
  UiUpdateType,
} from '@app/app/src/types';
import Pagination from '@atlassian/pagination';
import { usePagination } from '../hooks/usePagination';
import { OptionType } from '@atlaskit/select';
import { DateRangeInfo } from '@app/common';
import { ProductList } from './ProductList';
import { UpdateStatus } from './UpdateStatus';
import { UpdateImpactRating } from './UpdateImpactRating';
import { UpdateTags } from './UpdateTags';
import { UpdateChangeType } from './UpdateChangeType';
import { fg, featureGateKeys } from '@app/feature-gates';
import { useOneTrustContext } from '@app/onetrust';
import { UnreadDotCell } from './UnreadDotCell';
import { AIDisclaimer } from '@app/app-details/src/components';

const messages = defineMessages({
  emptyStateHeader: {
    id: 'appUpdatesTable.emptyState.header',
    defaultMessage: 'No release notes found',
    description: 'Header text for empty app updates table state',
  },
  emptyStateDescription: {
    id: 'appUpdatesTable.emptyState.description',
    defaultMessage:
      'No release notes match your filters. Adjust or clear your filters and try again.',
    description: 'Description text for empty app updates table state',
  },
  errorStateHeader: {
    id: 'appUpdatesTable.errorState.header',
    defaultMessage: 'Unable to load release notes',
    description: 'Header text for error app updates table state',
  },
  errorStateDescription: {
    id: 'appUpdatesTable.errorState.description',
    defaultMessage:
      "We couldn't load your release notes. Refresh the page to try again.",
    description: 'Description text for error app updates table state',
  },
  productHeader: {
    id: 'appUpdatesTable.column.product',
    defaultMessage: 'App',
    description: 'Column header for product/app name',
  },
  releaseNoteHeader: {
    id: 'appUpdatesTable.column.releaseNote',
    defaultMessage: 'Release note',
    description: 'Column header for release note',
  },
  statusHeader: {
    id: 'appUpdatesTable.column.status',
    defaultMessage: 'Status',
    description: 'Column header for status',
  },
  impactRatingHeader: {
    id: 'appUpdatesTable.column.impactRating',
    defaultMessage: 'Impact',
    description: 'Column header for AI-generated impact rating',
  },
  changeTypeHeader: {
    id: 'appUpdatesTable.column.changeType',
    defaultMessage: 'Change type',
    description: 'Column header for original (non-AI) change type',
  },
  contextualTagsHeader: {
    id: 'appUpdatesTable.column.contextualTags',
    defaultMessage: 'Contextual tags',
    description: 'Column header for AI-generated contextual tags',
  },
  rolloutScheduleHeader: {
    id: 'appUpdatesTable.column.rolloutSchedule',
    defaultMessage: 'Rollout schedule',
    description: 'Column header for rollout schedule date range',
  },
  adminCalloutText: {
    id: 'appUpdatesTable.adminCallout.text',
    defaultMessage:
      'For rollout schedules specific to your organization, visit <appUpdatesLink>App updates</appUpdatesLink> in <adminLink>Atlassian Administration</adminLink>. Organization admin access required.',
    description:
      'Callout below the table encouraging org admins to visit Atlassian Administration for org-specific rollout schedules. The text inside <appUpdatesLink> and <adminLink> tags are the visible link labels.',
  },
});

type AppUpdatesTableProps = {};

export const AppUpdatesTable: FunctionComponent<AppUpdatesTableProps> = () => {
  const { formatMessage } = useIntl();
  const { state } = useAppUpdatesContext();
  const { setCursor, setLimit } = usePagination();
  const { analyticsOptOut } = useOneTrustContext();
  const shouldShowUnreadIndicator =
    fg(featureGateKeys.unreadIndicator) && !analyticsOptOut;

  const onNextPageClicked = () => {
    // pagination component ensures button is disabled if no next page
    // but ts makes us do an extra check here
    if (state.updates.links.next) {
      setCursor(state.updates.links.next);
    }
  };
  const onPreviousPageClicked = () => {
    // pagination component ensures button is disabled if no previous page
    // but ts makes us do an extra check here
    if (state.updates.links.prev) {
      setCursor(state.updates.links.prev);
    }
  };
  const onPageSizeChange = (value: OptionType) => {
    setLimit(value.value as number);
  };

  const unreadIndicatorColumn: ColumnDef<
    AppUpdatesContextStateType['updates']['data'][0]
  > = {
    id: 'unreadDot',
    header: '',
    accessorKey: 'id',
    size: 24,
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original.firstPublishedAt) {
        return null;
      }

      return (
        <UnreadDotCell
          id={row.original.id}
          firstPublishedAt={row.original.firstPublishedAt}
        />
      );
    },
  };

  const columnDefs: ColumnDef<
    AppUpdatesContextStateType['updates']['data'][0]
  >[] = [
    ...(shouldShowUnreadIndicator ? [unreadIndicatorColumn] : []),
    {
      id: 'product',
      header: formatMessage(messages.productHeader),
      accessorKey: 'productTypes',
      size: 150,
      enableSorting: false,
      cell: ({ row }) => (
        <ProductList productTypes={row.original.productTypes} />
      ),
    },
    {
      id: 'title',
      header: formatMessage(messages.releaseNoteHeader),
      accessorKey: 'title',
      size: 400,
      enableSorting: false,
      cell: ({ row }) => (
        <Box
          xcss={xcss({
            whiteSpace: 'normal',
          })}
        >
          <RouterLink to={ROUTES.RELEASE_NOTE_DETAILS(row.original.id)}>
            <Text color="color.link" weight="regular">
              {row.original.title}
            </Text>
          </RouterLink>
        </Box>
      ),
    },
    {
      id: 'status',
      header: formatMessage(messages.statusHeader),
      accessorKey: 'status',
      size: 150,
      enableSorting: false,
      cell: ({ row }) => <UpdateStatus status={row.original.status} />,
    },
    {
      id: 'changeType',
      header: formatMessage(messages.changeTypeHeader),
      accessorKey: 'type',
      size: 130,
      enableSorting: false,
      cell: ({ row }) => <UpdateChangeType type={row.original.type} />,
    },
    ...(fg(featureGateKeys.aiImpactAndContextTags)
      ? [
          {
            id: 'impactRating',
            header: () => (
              <AIDisclaimer
                label={formatMessage(messages.impactRatingHeader)}
              />
            ),
            accessorKey: 'impactRating',
            enableSorting: false,
            cell: ({ row }: { row: { original: UiUpdateType } }) => (
              <UpdateImpactRating impactRating={row.original.impactRating} />
            ),
          },
          {
            id: 'contextualTags',
            header: () => (
              <AIDisclaimer
                label={formatMessage(messages.contextualTagsHeader)}
              />
            ),
            accessorKey: 'tags',
            size: 200,
            maxSize: 200,
            enableSorting: false,
            cell: ({ row }: { row: { original: UiUpdateType } }) => (
              <UpdateTags tags={row.original.tags} />
            ),
          },
        ]
      : []),
    {
      id: 'rolloutSchedule',
      header: formatMessage(messages.rolloutScheduleHeader),
      accessorKey: 'releaseStartDate',
      size: 180,
      enableSorting: false,
      minSize: 180,
      cell: ({ row }) => (
        <DateRangeInfo
          startDate={row.original.releaseStartDate}
          endDate={row.original.releaseEndDate}
          status={row.original.status}
        />
      ),
    },
  ];

  const tableState = state.updates.error
    ? TableState.error
    : state.updates.isLoading
    ? TableState.loading
    : TableState.loaded;

  return (
    <Stack space="space.200">
      <CharlieTable
        data={state.updates.data}
        columnDefs={columnDefs}
        manualSorting={false}
        tableState={tableState}
        emptyTableView={
          <EmptyState
            header={formatMessage(messages.emptyStateHeader)}
            description={formatMessage(messages.emptyStateDescription)}
          />
        }
        errorView={
          <EmptyState
            header={formatMessage(messages.errorStateHeader)}
            description={formatMessage(messages.errorStateDescription)}
          />
        }
        tableType="INVENTORY"
        manualPagination={true}
      />
      <Pagination
        hasPreviousPage={!!state.updates?.links?.prev}
        hasNextPage={!!state.updates?.links?.next}
        onNextPageClicked={onNextPageClicked}
        onPreviousPageClicked={onPreviousPageClicked}
        onPageSizeChange={onPageSizeChange}
      />
      {/* Admin callout — encourages org admins to visit Admin for org-specific rollout schedules */}
      <Flex
        justifyContent="center"
        xcss={xcss({
          backgroundColor: 'color.background.neutral',
          borderRadius: 'radius.small',
          paddingBlock: 'space.150',
          paddingInline: 'space.150',
        })}
      >
        <Text size="small" color="color.text.subtle">
          {formatMessage(messages.adminCalloutText, {
            appUpdatesLink: (chunks: ReactNode) => (
              <Link
                href="https://support.atlassian.com/organization-administration/docs/view-all-changes-to-an-atlassian-app/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
            adminLink: (chunks: ReactNode) => (
              <Link
                href="https://admin.atlassian.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
          })}
        </Text>
      </Flex>
    </Stack>
  );
};

export default AppUpdatesTable;
